// ============================================================================
// Operational Alerts Widget — computed signals across multiple tables that
// translate to "leadership action needed". Each row is a single counter
// with a tone (red/amber/neutral) so the page reads like a status panel.
//
// Sources:
//   • red_facilities, amber_facilities, facilities_missing_rag → facilities
//   • open_feedback, recurring_feedback, feedback_last_7d       → facility_feedback
//   • hiring_requests_open                                      → hiring_requests
//   • facilities_missing_geo                                    → facilities (lat/lng)
//
// Add new rows by extending OperationalAlerts in use-dashboard-data.ts and
// pushing a new ALERT_ROWS entry here. Tone is purely visual — the underlying
// data is still authoritative.
// ============================================================================

import type { OperationalAlerts } from './use-dashboard-data';
import { WidgetCard, WidgetSkeleton } from './widget-card';

interface Props {
  data: OperationalAlerts | null;
  loading: boolean;
}

type AlertTone = 'red' | 'amber' | 'neutral' | 'ok';

interface AlertRowSpec {
  key: keyof OperationalAlerts;
  label: string;
  // `tone` is a function so a counter can flip green/amber/red based on its
  // own value (e.g. open_feedback ≥ 30 is red, ≥ 10 amber, else neutral).
  tone: (value: number) => AlertTone;
}

const ALERT_ROWS: AlertRowSpec[] = [
  {
    key: 'red_facilities',
    label: 'Red facilities',
    tone: (n) => (n > 0 ? 'red' : 'ok'),
  },
  {
    key: 'amber_facilities',
    label: 'Amber facilities',
    tone: (n) => (n > 0 ? 'amber' : 'ok'),
  },
  {
    key: 'recurring_feedback',
    label: 'Recurring feedback',
    tone: (n) => (n > 0 ? 'red' : 'ok'),
  },
  {
    key: 'open_feedback',
    label: 'Open feedback issues',
    tone: (n) => (n >= 30 ? 'red' : n >= 10 ? 'amber' : 'neutral'),
  },
  {
    key: 'feedback_last_7d',
    label: 'New issues (7 days)',
    tone: (n) => (n >= 5 ? 'amber' : 'neutral'),
  },
  {
    key: 'hiring_requests_open',
    label: 'Open hiring requests',
    tone: (n) => (n > 0 ? 'amber' : 'ok'),
  },
  {
    key: 'facilities_missing_rag',
    label: 'Facilities without RAG',
    tone: (n) => (n > 0 ? 'amber' : 'ok'),
  },
  {
    key: 'facilities_missing_geo',
    label: 'Active facilities missing geo',
    tone: (n) => (n > 0 ? 'amber' : 'ok'),
  },
];

const TONE_DOT: Record<AlertTone, string> = {
  red: 'bg-rag-red',
  amber: 'bg-rag-amber',
  neutral: 'bg-st-neutral',
  ok: 'bg-rag-green',
};

const TONE_VALUE: Record<AlertTone, string> = {
  red: 'text-rag-red',
  amber: 'text-st-warn-fg',
  neutral: 'text-tcs-text-secondary',
  ok: 'text-st-ok-fg',
};

export function AlertsWidget({ data, loading }: Props) {
  if (loading && !data) {
    return (
      <WidgetCard title="Operational alerts" minHeight={300}>
        <WidgetSkeleton lines={6} />
      </WidgetCard>
    );
  }

  if (!data) {
    return (
      <WidgetCard title="Operational alerts" minHeight={300}>
        <p className="text-sm text-tcs-text-muted">No alert data available.</p>
      </WidgetCard>
    );
  }

  // Sort: red rows first, then amber, then neutral/ok. Within tone, retain
  // declaration order so the layout is stable across renders.
  const TONE_RANK: Record<AlertTone, number> = {
    red: 0,
    amber: 1,
    neutral: 2,
    ok: 3,
  };
  const rows = ALERT_ROWS.map((spec) => {
    const value = data[spec.key];
    const tone = spec.tone(value);
    return { ...spec, value, tone };
  }).sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]);

  const anyRed = rows.some((r) => r.tone === 'red');

  return (
    <WidgetCard
      title="Operational alerts"
      subtitle={
        anyRed ? 'Action needed today' : 'No urgent items'
      }
      minHeight={300}
    >
      <ul className="divide-y divide-tcs-border">
        {rows.map((r) => (
          <li
            key={r.key}
            className="flex items-center justify-between gap-3 py-2 text-sm"
          >
            <div className="flex items-center gap-2">
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${TONE_DOT[r.tone]}`}
                aria-hidden
              />
              <span className="text-tcs-text-secondary">{r.label}</span>
            </div>
            <span
              className={`tabular-nums font-medium ${TONE_VALUE[r.tone]}`}
            >
              {r.value}
            </span>
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
}
